import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { CivilNameInput } from '../../api/generated/model';
import { Field } from '../../design/primitives/Field';
import { clampSplitIndex, maskFor, splitOptionsFor } from './civilName';
import styles from './enrollment.module.css';

export interface CivilNameFieldProps {
  readonly value: CivilNameInput;
  readonly onChange: (value: CivilNameInput) => void;
}

/**
 * Entrada do nome civil com escolha visivel do corte da mascara.
 *
 * O contrato pede dois dados — o nome inteiro e onde cortar — e diz por que o
 * segundo existe: o backend nao pode adivinhar onde termina o nome de
 * tratamento, e a mascara e o que todo mundo, exceto o Super Admin, vera dessa
 * pessoa para sempre. Quem preenche o formulario e quem sabe.
 *
 * Por isso o corte nao e um campo numerico: cada opcao e rotulada **pela
 * mascara que ela produz**, entao escolher o corte e escolher a mascara, e a
 * escolha e revisavel enquanto o formulario estiver aberto. Um `maskSplitIndex`
 * numerico transferiria para quem digita a tarefa de simular a regra de cabeca,
 * e o erro so apareceria depois de gravado.
 */
export function CivilNameField({ value, onChange }: CivilNameFieldProps) {
  const { t } = useTranslation();
  const groupName = useId();

  const options = splitOptionsFor(value.civilName);
  const selected = clampSplitIndex(value.civilName, value.maskSplitIndex);
  const preview = maskFor(value.civilName, selected);

  const setName = (civilName: string): void => {
    onChange({ civilName, maskSplitIndex: clampSplitIndex(civilName, value.maskSplitIndex) });
  };

  return (
    <div className={styles.civilName}>
      <Field
        label={t('enrollment.civilName')}
        hint={t('enrollment.civilNameHint')}
        required
        value={value.civilName}
        autoComplete="off"
        onChange={(event) => setName(event.target.value)}
      />

      <fieldset className={styles.maskChoice}>
        <legend className={styles.maskLegend}>{t('enrollment.maskSplitLegend')}</legend>
        <p className={styles.maskHelp}>{t('enrollment.maskSplitHelp')}</p>

        {options.length === 0 ? (
          <p className={styles.maskEmpty}>{t('enrollment.maskNeedsTwoWords')}</p>
        ) : (
          <ul className={styles.maskOptions}>
            {options.map((option) => (
              <li key={option}>
                <label className={styles.maskOption}>
                  <input
                    type="radio"
                    name={groupName}
                    value={option}
                    checked={selected === option}
                    onChange={() => onChange({ civilName: value.civilName, maskSplitIndex: option })}
                  />
                  {/* O rotulo e a propria mascara: e ela que a pessoa escolhe. */}
                  <span className={styles.maskSample}>{maskFor(value.civilName, option)}</span>
                </label>
              </li>
            ))}
          </ul>
        )}

        {/*
          Repetir a escolha numa regiao viva nao e redundancia: quem navega por
          leitor de tela ouve o rotulo do radio ao mover o foco, mas quem chega
          por outro caminho — colar um nome maior, por exemplo — nao ouviria a
          mascara mudar debaixo da escolha ja feita.
        */}
        <p className={styles.maskPreview} aria-live="polite">
          {preview === null
            ? t('enrollment.maskNone')
            : t('enrollment.maskPreview', { mask: preview })}
        </p>
      </fieldset>
    </div>
  );
}

/** Nome civil vazio, com o corte no primeiro token — o caso mais comum. */
export function emptyCivilName(): CivilNameInput {
  return { civilName: '', maskSplitIndex: 1 };
}

/** Um nome so vira `CivilNameInput` valido quando o corte produz mascara. */
export function isCivilNameComplete(value: CivilNameInput): boolean {
  return maskFor(value.civilName, value.maskSplitIndex) !== null;
}
